import * as _ from 'lodash';
import type { Simplify } from 'type-fest';

import { isValidFetcher } from '../../shared/fetchers';
import {
  BlockTypes,
  Depths,
  isValidFixType,
  isValidSeverity,
  Visibilities,
} from '../../shared/organizationPackages/filters';
import { validSortOptions } from '../../shared/organizationPackages/sortOptions';
import { ProjectSource } from '../../typings/enums';
import type { BlockType, Depth, Visibility } from '../../shared/organizationPackages/filters';
import type { ProjectSources } from '../../typings/enums';
import type { HydrateStateQuery } from '../redux/packageInventory/types';

import { parsePaginationQueryParams } from './pagination';

const COUNT_MAX_FE = 50;

/**
 * Determine if a given depth is valid.
 */
export const isValidDepth = (maybeDepth: unknown): maybeDepth is Depth =>
  Depths.includes(maybeDepth as Depth);

/**
 * Determine if a given source is valid.
 */
export const isValidSource = (maybeSource: unknown): maybeSource is ProjectSources =>
  Object.values(ProjectSource).includes(maybeSource as ProjectSources);

/**
 * Determine if a given visibility is valid
 */
export const isValidVisibility = (maybeVisibility: unknown): maybeVisibility is Visibility =>
  Visibilities.includes(maybeVisibility as Visibility);

/**
 * Determine if a given blockType is valid
 */
export const isValidBlockType = (maybeBlockType: unknown): maybeBlockType is BlockType =>
  BlockTypes.includes(maybeBlockType as BlockType);

// Filters are flattened into the params. See below.
export type PackageInventoryQueryParams = Simplify<Omit<HydrateStateQuery, 'filter'> & HydrateStateQuery['filter']>;

/**
 * Unlike in other parts of the app, `filter`
 * is flattened, so instead of `filter[fetchers][0]=value`
 * we would have `fetchers[0]=value`.
 */
export const flattenFilters = (query: HydrateStateQuery): PackageInventoryQueryParams => {
  if (!query.filter || _.isEmpty(query)) {
    return query;
  }
  const { filter, ...rest } = query;

  return {
    ...filter,
    ...rest,
  };
};

/**
 * Parse out the query paramaters, from the qs.parse in useSyncStateAndURLParams.
 * Note that this will need to be updated every time we add a filter, since
 * filters are just spread onto the query params.
 */
export const parseQueryParams = (query: Partial<PackageInventoryQueryParams>): HydrateStateQuery => {
  const parsed: Partial<HydrateStateQuery> = parsePaginationQueryParams(query);

  if (query.count) {
    const numericCount = Number(query.count);
    //  If it's NaN/some garbage, we just ignore and don't set the count.
    if (Number.isFinite(numericCount)) {
      parsed.count = numericCount > COUNT_MAX_FE
        ? COUNT_MAX_FE
        : numericCount;
    }
  }

  if (query.sort) {
    const isValidSortOption = validSortOptions.includes(query.sort);
    if (isValidSortOption) {
      parsed.sort = query.sort;
    }
  }

  //  Un-flatten filters
  if (query.fetchers) {
    const validFilters = query.fetchers?.filter(isValidFetcher) ?? [];
    if (validFilters?.length > 0) {
      parsed.filter = {
        ...(parsed.filter || {}),
        fetchers: validFilters,
      };
    }
  }

  if (query.locators) {
    const validLocators = query.locators.filter(l => _.isString(l));
    if (validLocators.length > 0) {
      parsed.filter = {
        ...(parsed.filter || {}),
        locators: validLocators,
      };
    }
  }

  if (query.packageLabels) {
    const validPackageLabels = query.packageLabels.filter(l => _.isString(l));
    if (validPackageLabels.length > 0) {
      parsed.filter = {
        ...(parsed.filter || {}),
        packageLabels: validPackageLabels,
      };
    }
  }

  if (query.packageName) {
    const trimmedSearch = query.packageName.trim();
    if (trimmedSearch) {
      parsed.filter = {
        ...(parsed.filter || {}),
        packageName: trimmedSearch,
      };
    }
  }

  if (query.depth) {
    const validDepths = query.depth?.filter(isValidDepth) ?? [];
    if (validDepths?.length > 0) {
      parsed.filter = {
        ...(parsed.filter || {}),
        depth: validDepths,
      };
    }
  }

  if (query.sources) {
    const validSources = query.sources?.filter(isValidSource) ?? [];
    if (validSources?.length > 0) {
      parsed.filter = {
        ...(parsed.filter || {}),
        sources: validSources,
      };
    }
  }

  if (query.labels) {
    const labels = query.labels.map(l => l.trim()).filter(l => l.length > 0);
    if (labels.length > 0) {
      parsed.filter = {
        ...(parsed.filter || {}),
        labels,
      };
    }
  }

  if (query.projectName) {
    const projectName = query.projectName.trim();
    if (projectName) {
      parsed.filter = {
        ...(parsed.filter || {}),
        projectName,
      };
    }
  }

  if (query.visibility) {
    const validVisibilities = query.visibility?.filter(isValidVisibility) ?? [];
    if (validVisibilities.length > 0) {
      parsed.filter = {
        ...(parsed.filter || {}),
        visibility: validVisibilities,
      };
    }
  }

  if (query.blockTypes) {
    const validBlockTypes = query.blockTypes?.filter(isValidBlockType) ?? [];
    if (validBlockTypes.length > 0) {
      parsed.filter = {
        ...(parsed.filter || {}),
        blockTypes: validBlockTypes,
      };
    }
  }

  if (query.cve) {
    const cve = query.cve.trim();
    if (cve) {
      parsed.filter = {
        ...(parsed.filter || {}),
        cve,
      };
    }
  }

  if (query.cwes) {
    parsed.filter = {
      ...(parsed.filter || {}),
      cwes: query.cwes,
    };
  }

  if (query.fixTypes) {
    const validFixTypes = query.fixTypes.filter(isValidFixType) ?? [];
    if (validFixTypes.length > 0) {
      parsed.filter = {
        ...(parsed.filter || {}),
        fixTypes: validFixTypes,
      };
    }
  }

  if (query.severities) {
    const validSeverities = query.severities.filter(isValidSeverity) ?? [];
    if (validSeverities.length > 0) {
      parsed.filter = {
        ...(parsed.filter || {}),
        severities: validSeverities,
      };
    }
  }

  return parsed as HydrateStateQuery;
};
